import { FC, useEffect, useState } from 'react';

import { ColorPalette } from './color-palette';
import { ColorGrid } from './color-grid';

const INITIAL_COLORS = ['#D90018', '#42AA02', '#93532C', '#FFCD0B', '#51007E', '#013C7E', '#817F85', '#010001'];

export const ColorCodeClient: FC<{ sendColorCodeResult?: (colors: string[]) => void }> = ({
  sendColorCodeResult,
}) => {
  const [availableColors, setAvailableColors] = useState(INITIAL_COLORS);
  const [selectedColors, setSelectedColors] = useState<(string | null)[]>(Array(8).fill(null));
  const [draggedColor, setDraggedColor] = useState<string | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [tapSelectedColor, setTapSelectedColor] = useState<string | null>(null);
  const [lastTapTime, setLastTapTime] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // === DESKTOP (drag & drop) ===
  const handleDragStart = (color: string) => {
    if (!isMobile) setDraggedColor(color);
  };
  const handleDragOver = (e: React.DragEvent) => {
    if (!isMobile) e.preventDefault();
  };
  const handleDrop = (index: number) => {
    if (isMobile || !draggedColor) return;
    // Проверяем, занят ли слот
    if (selectedColors[index] !== null) return;
    
    setSelectedColors((prev) => {
      const updated = [...prev];
      updated[index] = draggedColor;

      return updated;
    });
    setAvailableColors((prev) => prev.filter((c) => c !== draggedColor));
    setDraggedColor(null);
    setHoverIndex(null);
  };

  const handleDoubleClick = (index: number) => {
    const color = selectedColors[index];
    if (!color) return;
    setAvailableColors((prev) => [...prev, color]);
    setSelectedColors((prev) => prev.map((c, i) => (i === index ? null : c)));
  };

  const handleTapColor = (color: string) => {
    if (!isMobile) return;
    setTapSelectedColor((prev) => (prev === color ? null : color));
  };

  const handleTapSlot = (index: number) => {
    if (!isMobile) return;

    const now = Date.now();

    if (lastTapTime && now - lastTapTime < 300) {
      const color = selectedColors[index];
      if (color) {
        setAvailableColors((prev) => [...prev, color]);
        setSelectedColors((prev) => prev.map((c, i) => (i === index ? null : c)));
      }
      setLastTapTime(null);

      return;
    }

    setLastTapTime(now);

    if (tapSelectedColor) {
      // Проверяем, занят ли слот
      if (selectedColors[index] !== null) {
        setTapSelectedColor(null);
        return;
      }
      
      setSelectedColors((prev) => {
        const updated = [...prev];
        updated[index] = tapSelectedColor;

        return updated;
      });
      setAvailableColors((prev) => prev.filter((c) => c !== tapSelectedColor));
      setTapSelectedColor(null);
    }
  };

  const submitSelection = () => {
    const colors = selectedColors.filter((c): c is string => c !== null);
    sendColorCodeResult?.(colors);
    setIsSubmitted(true);
  };

  const isComplete = selectedColors.every((c) => c !== null);

  return (
    <div className="h-full w-full sm:w-[472px] lg:w-[711px] flex flex-row pr-[0.25rem] rounded-[16px]">
      <div className="w-full flex flex-col bg-[#525458] text-white font-comfortaa p-6 overflow-y-auto white-scroll">
        <h2 className="mb-5 font-bold text-2xl xl:text-3xl">Цветовой код</h2>
        <p className="mb-6 text-sm lg:text-[18px] leading-relaxed font-manrope">
          Разложите эти 8 цветов по порядку: от самого приятного для вас в данный момент до самого неприятного.
          Доверьтесь первому эмоциональному восприятию здесь и сейчас.
        </p>
        {!isMobile && (
          <>
            <ColorPalette colors={availableColors} onDragStart={handleDragStart} />

            <ColorGrid
              selectedColors={selectedColors}
              onDrop={handleDrop}
              onDoubleClick={handleDoubleClick}
              onDragOver={handleDragOver}
              hoverIndex={hoverIndex}
              setHoverIndex={setHoverIndex}
            />
          </>
        )}
        {isMobile && (
          <>
            <div className="grid grid-cols-4 grid-rows-2 gap-2 mb-4">
              {Array.from({ length: 8 }, (_, index) => {
                const color = availableColors[index];
                if (!color) {
                  return <div key={`empty-${index}`} className="w-full aspect-square" />;
                }
                return (
                  <button
                    key={color}
                    onClick={() => handleTapColor(color)}
                    className={`w-full aspect-square rounded-[10px] transition-transform duration-150 ${
                      tapSelectedColor === color ? 'scale-110 ring-4 ring-white' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                );
              })}
            </div>

            <div className="grid grid-cols-4 grid-rows-2 gap-2 mb-6 w-full">
              {selectedColors.map((color, i) => (
                <button
                  key={i}
                  onClick={() => handleTapSlot(i)}
                  className={`w-full aspect-square rounded-[10px] border-2 ${
                    color ? 'border-none' : 'border-[#828282]'
                  }`}
                  style={{ backgroundColor: color || 'transparent' }}
                >
                  {!color && <span className="text-lg font-bold text-white/70">{i + 1}</span>}
                </button>
              ))}
            </div>
          </>
        )}
        {!isSubmitted && (
          <div className="w-full flex justify-center mx-auto pt-1 pb-4">
            <button
              className={`w-full py-3 rounded-[10px] font-bold text-base transition-colors ${
                isComplete
                  ? 'bg-[#C00000] text-white hover:bg-[#a00000] cursor-pointer'
                  : 'bg-[#C00000]/50 text-white/50 cursor-not-allowed'
              }`}
              onClick={isComplete ? submitSelection : undefined}
              disabled={!isComplete}
            >
              Выбрать
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
